import { useEffect } from "react";
import { useProgress } from "./progress";
import {
  ACTIVITY_TICK_MS,
  ActivityTracker,
  browserActivityClock,
  type ActivityClock,
} from "../lib/activityClock";
import { subscribePreviewActivity } from "../lib/previewActivity";

export function useActivity(
  enabled: boolean,
  clock: ActivityClock = browserActivityClock,
) {
  useEffect(() => {
    if (!enabled) return;
    const tracker = new ActivityTracker(clock, !document.hidden);
    const touch = (event: Event) => {
      if (event.isTrusted) tracker.interact();
    };
    const tick = () => {
      const { seconds, days } = tracker.flush();
      if (!seconds) return;
      useProgress.getState().update((data) => ({
        ...data,
        activeSeconds: data.activeSeconds + seconds,
        days: [...new Set([...data.days, ...days])],
      }));
    };
    const visibility = () => {
      tracker.setVisible(!document.hidden);
      tick();
      // App's older visibility listener can flush before this hydrated hook.
      // Commit again after settling time; the store serializes both snapshots.
      if (document.hidden) void useProgress.getState().flush();
    };
    const unsubscribe = subscribePreviewActivity(() => tracker.interact());
    const interval = setInterval(tick, ACTIVITY_TICK_MS);
    // Programmatic scroll also produces trusted scroll events. Only input renews.
    const events = ["pointerdown", "keydown", "wheel"];
    for (const event of events)
      window.addEventListener(event, touch, { capture: true, passive: true });
    document.addEventListener("visibilitychange", visibility);
    return () => {
      clearInterval(interval);
      tick();
      unsubscribe();
      for (const event of events)
        window.removeEventListener(event, touch, true);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [enabled, clock]);
}
